import CondorSpinner from '../CondorSpinner';
import { SectionTitleText } from '../../StyledComponents/Typography';
import cutecondor from '../../assets/cute-condor.png';
import React, { useMemo } from 'react';
import { StyledDataGrid } from '../../StyledComponents/Table';
import { GridColDef } from '@mui/x-data-grid-pro';
import { GridValidRowModel } from '@mui/x-data-grid';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { ButtonRules, StyledButton } from '@canopysecurity/component-library';
import { getDeployments } from '../../services/DeploymentService';
import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '../../hooks/useApiClient';

const TableContainer = styled.div`
  margin-top: 3%;
  padding: 0 5% 0 5%;
  height: 40vh;
  margin-bottom: 4em;

  .header-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 16px;
  }

  .button-container {
    display: flex;
    justify-content: flex-end;
    column-gap: 8px;
  }
`;

const DeploymentDetailsButton = React.memo<{ deploymentId: string }>(
  ({ deploymentId }) => {
    const navigate = useNavigate();
    return (
      <StyledButton
        onPress={() => {
          navigate(`/details/${deploymentId}`);
        }}
        buttonRules={ButtonRules.primaryA}
        testID={`details-button-${deploymentId}`}
      >
        Details
      </StyledButton>
    );
  },
);
DeploymentDetailsButton.displayName = 'DeploymentDetailsButton';
const NoRowsOverlay = React.memo(() => (
  <div>
    <img
      src={cutecondor}
      alt="No deployments to display"
      style={{ maxHeight: '70%' }}
    />
  </div>
));
NoRowsOverlay.displayName = 'NoRowsOverlay';

interface DeploymentPackagesProps {
  status: 'active' | 'inactive';
}

interface DeploymentTableProps {
  id: string;
  releaseDate: string;
  releaseVersion: string;
  lastDeployedBy: string;
  forceInstall: boolean;
}

export default function DeploymentPackages({
  status,
}: DeploymentPackagesProps) {
  const { token } = useApiClient();

  const { data, isLoading } = useQuery({
    queryKey: ['deployments', status],
    queryFn: () => getDeployments(status),
    enabled: !!token,
  });

  const deployments = useMemo(() => {
    return data?.map((deployment: any) => {
      return {
        id: deployment?.id,
        releaseDate: deployment?.createTime
          ? new Date(deployment.createTime).toLocaleDateString()
          : 'unknown',
        releaseVersion: deployment?.release?.semver,
        lastDeployedBy: deployment?.lastDeployedBy ?? 'unknown',
        forceInstall: deployment?.forceInstall ?? false,
      };
    });
  }, [data]);

  const createColumns = (): GridColDef<DeploymentTableProps>[] => [
    {
      field: 'releaseVersion',
      width: 200,
      renderHeader: () => <strong>Release Version</strong>,
    },
    {
      field: 'releaseDate',
      width: 200,
      renderHeader: () => <strong>Release Date</strong>,
    },
    {
      field: 'lastDeployedBy',
      flex: 1,
      renderHeader: () => <strong>Last Deployed By</strong>,
    },
    {
      field: 'forceInstall',
      width: 150,
      renderHeader: () => <strong>Force Install</strong>,
      renderCell: (params) => (
        <span>{params.row.forceInstall ? 'Yes' : 'No'}</span>
      ),
    },
    {
      field: 'details',
      width: 200,
      headerName: '',
      renderCell: (params) => (
        <DeploymentDetailsButton deploymentId={params.row.id} />
      ),
    },
  ];

  const columns = useMemo(() => createColumns(), [data]);

  const sectionTitle = `Deployed OTA Updates - ${
    status === 'active' ? 'Active' : 'Inactive'
  } Deployments`;

  return (
    <>
      {isLoading ? (
        <CondorSpinner />
      ) : (
        <TableContainer>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingBottom: '16px',
            }}
          >
            <SectionTitleText>{sectionTitle}</SectionTitleText>
            <div
              style={{
                marginLeft: 'auto',
                cursor: 'pointer',
                paddingRight: '8px',
              }}
            ></div>
          </div>
          <StyledDataGrid
            rows={deployments}
            columns={columns as GridColDef<GridValidRowModel>[]}
            getRowClassName={(params) =>
              params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
            }
            slots={{
              noRowsOverlay: NoRowsOverlay,
            }}
            pageSizeOptions={[5, 10, 50, 100]}
          />
        </TableContainer>
      )}
    </>
  );
}
